import React from 'react';
import { X } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchableTagSelect from './SearchableTagSelect';
import { TAG_TYPES, TAG_STYLES, TAG_FIELD_MAP, FIELD_TO_TAG_TYPE } from '../hooks/useEntityTagging';

/**
 * Reusable tag input component for entities (officers, artifacts, rituals, policies, capabilities).
 * Works with useEntityTagging hook to eliminate 12+ duplicate tag selection implementations.
 * 
 * Usage:
 * const tagging = useEntityTagging({ officers, artifacts, rituals, policies, capabilities, currentUserEmail });
 * 
 * <EntityTagInput
 *   tags={tagging.tags}
 *   onAddTag={tagging.addTag}
 *   onRemoveTag={tagging.removeTag}
 *   getTagOptions={tagging.getTagOptions}
 *   currentTagType={tagging.currentTagType}
 *   onTagTypeChange={tagging.setCurrentTagType}
 *   officers={officers}
 * />
 */

export default function EntityTagInput({
  tags,
  onAddTag,
  onRemoveTag,
  getTagOptions,
  currentTagType,
  onTagTypeChange,
  officers = [],
  tagTypes = TAG_TYPES,
  placeholder = 'Add tag...',
  className = '',
  compact = false
}) {
  // Get display label for a tag
  const getTagLabel = (tagType, value) => {
    if (tagType === 'Officer') {
      const officer = officers.find(o => o.user_email === value);
      return officer?.callsign || value;
    }
    return value;
  };

  // Flatten tags for display
  const allTags = Object.entries(tags).flatMap(([fieldName, values]) => {
    const tagType = FIELD_TO_TAG_TYPE[fieldName];
    if (!tagType || !values) return [];
    
    return values.map(value => ({
      type: tagType,
      value,
      label: getTagLabel(tagType, value),
      style: TAG_STYLES[tagType],
      fieldName
    }));
  });

  const handleTagSelect = (value) => {
    if (value && currentTagType) {
      onAddTag(currentTagType, value);
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Display selected tags */}
      <div className={`flex flex-wrap gap-1 ${compact ? 'min-h-[28px]' : 'min-h-[42px]'} p-2 bg-slate-900 rounded border border-slate-700`}>
        {allTags.length === 0 ? (
          <span className="text-slate-600 text-xs italic">No tags selected</span>
        ) : (
          allTags.map((tag, idx) => (
            <Badge 
              key={`${tag.fieldName}-${idx}`} 
              variant="outline" 
              className={`text-xs ${tag.style}`}
            >
              {tag.label} ({tag.type})
              <button
                type="button"
                onClick={() => onRemoveTag(tag.type, tag.value)}
                className="ml-1 hover:text-red-400"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))
        )}
      </div>

      {/* Tag type and value selection */}
      <div className="flex gap-2">
        <Select
          value={currentTagType}
          onValueChange={(type) => {
            onTagTypeChange(type);
          }}
        >
          <SelectTrigger className={`bg-slate-950 border-slate-800 ${compact ? 'h-8 text-xs w-28' : 'h-9 text-sm w-32'}`}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {tagTypes.map(type => (
              <SelectItem key={type} value={type}>{type}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {currentTagType && (
          <SearchableTagSelect
            options={getTagOptions(currentTagType)}
            value=""
            onValueChange={handleTagSelect}
            placeholder={`Select ${currentTagType.toLowerCase()}...`}
            emptyMessage={`No ${currentTagType.toLowerCase()}s found.`}
            className={`flex-1 ${compact ? 'h-8 text-xs' : 'h-9 text-sm'}`}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Read-only tag display component
 */
export function EntityTagDisplay({ 
  tags, 
  officers = [],
  maxVisible = Infinity,
  showType = true,
  size = 'sm',
  className = '' 
}) {
  const getTagLabel = (tagType, value) => {
    if (tagType === 'Officer') {
      const officer = officers.find(o => o.user_email === value);
      return officer?.callsign || value;
    }
    return value;
  };

  const allTags = Object.entries(tags || {}).flatMap(([fieldName, values]) => {
    const tagType = FIELD_TO_TAG_TYPE[fieldName];
    if (!tagType || !values) return [];
    
    return values.map(value => ({
      type: tagType,
      value,
      label: getTagLabel(tagType, value),
      style: TAG_STYLES[tagType]
    }));
  });

  if (allTags.length === 0) return null;

  const visibleTags = allTags.slice(0, maxVisible);
  const hiddenCount = allTags.length - maxVisible;

  const sizeClasses = {
    xs: 'text-[10px] py-0 px-1.5',
    sm: 'text-xs py-0.5 px-2',
    md: 'text-sm py-1 px-2.5'
  };

  return (
    <div className={`flex flex-wrap gap-1 ${className}`}>
      {visibleTags.map((tag, idx) => (
        <Badge 
          key={idx} 
          variant="outline" 
          className={`${tag.style} ${sizeClasses[size]}`}
          title={showType ? `${tag.label} (${tag.type})` : tag.label}
        >
          {tag.label}
          {showType && ` (${tag.type})`}
        </Badge>
      ))}
      {hiddenCount > 0 && (
        <Badge variant="outline" className={`text-slate-400 border-slate-600 ${sizeClasses[size]}`}>
          +{hiddenCount} more
        </Badge>
      )}
    </div>
  );
}

/**
 * Simplified single-type tag input (when you only need one type)
 */
export function SingleTypeTagInput({
  tagType,
  tags,
  onAddTag,
  onRemoveTag,
  getTagOptions,
  officers = [],
  placeholder,
  className = ''
}) {
  const fieldName = TAG_FIELD_MAP[tagType];
  const values = tags[fieldName] || [];
  const style = TAG_STYLES[tagType];

  const getTagLabel = (value) => {
    if (tagType === 'Officer') {
      const officer = officers.find(o => o.user_email === value);
      return officer?.callsign || value;
    }
    return value;
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex flex-wrap gap-1 min-h-[32px] p-2 bg-slate-900 rounded border border-slate-700">
        {values.length === 0 ? (
          <span className="text-slate-600 text-xs italic">None selected</span>
        ) : (
          values.map((value, idx) => (
            <Badge key={idx} variant="outline" className={`text-xs ${style}`}>
              {getTagLabel(value)}
              <button
                type="button"
                onClick={() => onRemoveTag(tagType, value)}
                className="ml-1 hover:text-red-400"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))
        )}
      </div>
      <SearchableTagSelect
        options={getTagOptions(tagType)}
        value=""
        onValueChange={(value) => onAddTag(tagType, value)}
        placeholder={placeholder || `Select ${tagType.toLowerCase()}...`}
        emptyMessage={`No ${tagType.toLowerCase()}s found.`}
        className="h-9"
      />
    </div>
  );
}